import * as THREE from 'three';
import BasicObject from './BasicObject';
import { DirectionSloppedType, LineIntersType, SlopedSurfaceType } from '../types';
import { TextObject } from './TextObject';

interface ConstructorProps {
  contour: Array<THREE.Vector3>;
  angleSloped: number;
  direction: DirectionSloppedType;
}
interface PointsSquareType {
  p0: THREE.Vector3;
  p1: THREE.Vector3;
  p2: THREE.Vector3;
  p3: THREE.Vector3;
}
export class SlopedSurface extends BasicObject implements SlopedSurfaceType {
  private pointsSquare?: PointsSquareType;
  private arrowDirection: THREE.ArrowHelper | null = null;
  private textGrad: TextObject | null = null;
  contour: Array<THREE.Vector3>;
  angleSloped;
  direction: DirectionSloppedType;
  material = new THREE.MeshBasicMaterial({
    color: '#ff00ff',
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0,
  });
  constructor({ contour, angleSloped, direction }: ConstructorProps) {
    super();
    this.type = 'sloped_surface';
    this.name = 'sloped_surface';
    this.contour = contour;
    this.angleSloped = angleSloped;
    this.direction = direction;

    this.buildGeometry();
    this.createDirectionSlopedArrow();
  }

  buildGeometry() {
    super.buildGeometry();
    this.geometry = new THREE.Geometry();
    for (let i = 0; i < this.contour.length; i++) {
      const point = this.contour[i].clone();
      this.geometry.vertices.push(point);
    }
    const points2d: Array<THREE.Vector2> = this.contour.map(
      point => new THREE.Vector2(point.x, point.z)
    );

    const triangles = THREE.ShapeUtils.triangulateShape(points2d, []);
    for (let i = 0; i < triangles.length; i++) {
      this.geometry.faces.push(new THREE.Face3(triangles[i][0], triangles[i][2], triangles[i][1]));
    }
  }
  getPlanesZeroIntersected(): Array<THREE.Mesh> {
    const geom = this.sloppingSurface();
    geom.computeFaceNormals();
    return [new THREE.Mesh(geom, this.material)];
  }
  updateSlopedArrow() {
    if (this.arrowDirection) {
      this.remove(this.arrowDirection);
    }
    if (this.textGrad) {
      this.textGrad.removeMesh();
    }
    this.createDirectionSlopedArrow();
  }
  updateTextGrad() {
    if (this.textGrad) {
      this.textGrad.text = `${this.angleSloped}°`;
      this.textGrad.rebuildGeometry();

      // this.getHeightsPoints();
    }
  }
  getHeightsPoints(): Array<number> {
    const heights: Array<number> = [];
    const line = this.getHelperLine();
    const ray = new THREE.Ray();
    const contourZero = this.contour.map(v => v.clone().setY(0));
    contourZero.forEach((v, i) => {
      const pointLine = new THREE.Vector3();
      ray.set(v, new THREE.Vector3(0, -1, 0));
      ray.distanceSqToSegment(line.start, line.end, undefined, pointLine);
      v.y = pointLine.y;
      heights.push(v.distanceTo(this.contour[i]));
    });
    return heights;
  }
  removeMesh() {
    if (this.textGrad) {
      this.textGrad.removeMesh();
    }
    if (this.arrowDirection) {
      this.remove(this.arrowDirection);
    }
    super.removeMesh(this);
  }

  private createDirectionSlopedArrow(): void {
    const { p0, p1, p3 } = this.getPointsSquare();
    const offsetArrow = 0.5;
    const offsetText = 0.9;
    let length = 0;
    const direct = new THREE.Vector3();
    const point = new THREE.Vector3();
    const posText = new THREE.Vector3();

    switch (this.direction) {
      case 'bottomTop':
        direct.copy(p0.clone().sub(p3)).normalize();
        point.copy(p3);
        point.x -= offsetArrow;
        posText.copy(p0);
        posText.x -= offsetText;
        length = p0.distanceTo(p3);
        break;
      case 'topBottom':
        direct.copy(p3.clone().sub(p0)).normalize();
        point.copy(p0);
        point.x -= offsetArrow;
        posText.copy(p3);
        posText.x -= offsetText;
        length = p0.distanceTo(p3);
        break;
      case 'leftRight':
        // direct = p1.clone().sub(p0).normalize();
        direct.copy(p1.clone().sub(p0)).normalize();
        point.copy(p0);
        point.z -= offsetArrow;
        posText.copy(p1);
        posText.z -= 0.7;
        length = p0.distanceTo(p1);
        break;
      case 'rightLeft':
        direct.copy(p0.clone().sub(p1)).normalize();
        point.copy(p1);
        point.z -= offsetArrow;
        posText.copy(p0);
        posText.z -= 0.7;
        length = p0.distanceTo(p1);
        break;
    }

    const arrow = new THREE.ArrowHelper(direct.clone(), point, length, '#000000', 0.3, 0.1);
    const textObject = new TextObject(`${this.angleSloped}°`, 0.12);
    textObject.setCenterPosition();
    textObject.position.copy(posText);
    this.arrowDirection = arrow;
    this.textGrad = textObject;

    this.add(arrow, textObject);
  }
  private getPointsSquare(): PointsSquareType {
    if (this.pointsSquare) {
      return { ...this.pointsSquare };
    }
    const pointsX = this.contour.map(point => point.x);
    const pointsZ = this.contour.map(point => point.z);
    const minX = Math.min(...pointsX);
    const minZ = Math.min(...pointsZ);
    const maxX = Math.max(...pointsX);
    const maxZ = Math.max(...pointsZ);
    const p0 = new THREE.Vector3(minX, 0, minZ);
    const p1 = new THREE.Vector3(maxX, 0, minZ);
    const p2 = new THREE.Vector3(maxX, 0, maxZ);
    const p3 = new THREE.Vector3(minX, 0, maxZ);
    this.pointsSquare = { p0, p1, p2, p3 };

    return { p0, p1, p2, p3 };
  }
  private sloppingSurface(): THREE.Geometry {
    const line = this.getHelperLine();
    const ray = new THREE.Ray();
    const geometry = this.geometry.clone();
    (geometry as THREE.Geometry).vertices.forEach(v => {
      const pointLine = new THREE.Vector3();
      ray.set(v, new THREE.Vector3(0, -1, 0));
      ray.distanceSqToSegment(line.start, line.end, undefined, pointLine);
      v.y = pointLine.y;
    });
    (geometry as THREE.Geometry).verticesNeedUpdate = true;
    return geometry as THREE.Geometry;
  }
  private getHelperLine(): LineIntersType {
    const { p0, p1, p2, p3 } = this.getPointsSquare();

    const line = {
      start: new THREE.Vector3(),
      end: new THREE.Vector3(),
    };
    switch (this.direction) {
      case 'leftRight':
        line.start.copy(p0);
        line.end.copy(p1);
        break;
      case 'rightLeft':
        line.start.copy(p2);
        line.end.copy(p3);
        break;
      case 'topBottom':
        line.start.copy(p1);
        line.end.copy(p2);
        break;
      case 'bottomTop':
        line.start.copy(p3);
        line.end.copy(p0);
        break;
    }

    const directLine = line.end.clone().sub(line.start);
    const distance = line.end.distanceTo(line.start) * 2;
    const directLine90 = directLine.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
    directLine.normalize();
    directLine90.normalize();

    const sloppedDirect = directLine
      .clone()
      .applyAxisAngle(directLine90, THREE.MathUtils.degToRad(this.angleSloped));
    line.end.copy(line.start.clone().add(sloppedDirect.clone().multiplyScalar(distance)));

    return line;
  }
}
